import type { FunctionComponent } from 'react'
import React, { useEffect, useState } from 'react'
import { createBreakpoint } from 'react-use'
import { ArticleCard, CardRow, Grid, GridItem, Typography } from '@which/seatbelt'
import type { ImageWithSources } from '@which/shared'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import type {
  Header,
  Link,
  PicksArticleCard,
  PicksCardItem,
  PicksContent,
  PicksContentItem,
  PicksProductCard,
  PicksPromo,
} from '../../../../generated/frontend'
import { JumplinksInline } from '../../../../shared'
import { componentMap } from '../../../../shared/renderers/component-map'
import type { PicksAuthor } from '..'
import { Author, Card, Header as PicksHeader, InlineArticleLinks, RelatedLinks } from '..'
import styles from './Landing.module.scss'

const useBreakpoint = createBreakpoint({ mobile: 320, tablet: 768, desktop: 1024 })

export interface Props {
  header: Header
  contentItems: PicksContent[]
  relatedArticleLinks: Link[]
  authors: (PicksAuthor & { image: ImageWithSources })[]
}
export const Landing: FunctionComponent<Props> = ({
  header,
  contentItems,
  relatedArticleLinks,
  authors,
}) => {
  const renderAuthors = authors.length === 1 && typeof authors[0].about === 'string'
  const [author] = authors
  const [isMobile, setIsMobile] = useState(false)

  const breakpoint = useBreakpoint()

  useEffect(() => {
    setIsMobile(breakpoint === 'mobile')
  }, [breakpoint])

  return (
    <Grid>
      <GridItem className={styles.shopHeader}>
        <PicksHeader {...header} tag="Which? Energy" />
      </GridItem>
      <GridItem>
        <JumplinksInline links={getJumpLinkData(contentItems)} className={styles.picksJumplinks} />
      </GridItem>
      <GridItem>
        <div className={styles.shopSubHeader}>
          <div className={styles.shopSubHeaderWrapper}>
            {renderAuthors && (
              <Author
                jobTitle={author?.jobTitle}
                name={author?.name}
                src={author?.image?.src}
                sources={author?.sources}
                about={author?.about}
                isMobile={isMobile}
                renderDisclaimer={false}
              />
            )}
          </div>
        </div>
      </GridItem>
      {contentItems?.map((sectionItem, index) => {
        const pickContentItem = sectionItem as PicksContentItem
        if (isPicksPromoItem(sectionItem)) {
          const { component, props } = sectionItem
          const Component = componentMap[component]

          return (
            <GridItem
              className={classnames(styles.shopSection, styles['shopSection--promo'])}
              key={`promo-${component}-${index}`}
            >
              <div className={styles.promoHolder}>
                <Component {...props} />
              </div>
            </GridItem>
          )
        }

        if (isPicksContentItem(sectionItem)) {
          return (
            <GridItem
              className={styles.shopSection}
              key={`${pickContentItem.title}-${index}`}
              id={pickContentItem.jumpLinks?.id}
            >
              <Typography tag="h2" textStyle="sb-text-heading-page-title">
                {pickContentItem.title}
              </Typography>
              <Typography
                textStyle="sb-text-heading-standfirst"
                className={styles.shopSectionIntro}
              >
                {pickContentItem.intro}
              </Typography>
              {sectionItem.inlineArticles.length > 0 && (
                <InlineArticleLinks sectionItem={sectionItem} onClickHandler={onClickHandler} />
              )}
              <CardRow
                includeGutters={false}
                className={styles.shopCardRow}
                {...(pickContentItem.productListingLink?.href &&
                  pickContentItem.productListingLink.label && {
                    link: {
                      text: pickContentItem.productListingLink.label,
                      href: pickContentItem.productListingLink.href,
                    },
                  })}
              >
                {pickContentItem.items?.map((card, cardIndex) => {
                  if (isPicksProductCard(card)) {
                    return <Card {...card} key={`${card.title}-${cardIndex}`} />
                  }

                  if (isPicksArticleCard(card)) {
                    return (
                      <ArticleCard
                        key={`${card.title}-${cardIndex}`}
                        className={styles.shopArticleCard}
                        title={card.title}
                        href={card.link?.href}
                        strapline={card.description}
                        image={card.image?.src}
                        imageAlt={card.image?.alt}
                        imageSources={card.image?.sources}
                        clampStrapline={false}
                      />
                    )
                  }
                })}
              </CardRow>
            </GridItem>
          )
        }
      })}

      <GridItem>
        <div className={styles.shopFooter}>
          {relatedArticleLinks.length > 0 && (
            <div className={styles.shopFooterRelatedLinks}>
              <RelatedLinks relatedLinks={relatedArticleLinks} />
            </div>
          )}
        </div>
      </GridItem>
    </Grid>
  )
}

const getJumpLinkData = (contentItems: PicksContent[]) =>
  contentItems.filter(isPicksContentItem).map(({ jumpLinks: { id, label } }) => ({ label, id }))

const isPicksContentItem = (content: PicksContent): content is PicksContentItem =>
  content.__typename === 'PicksContentItem'

const isPicksPromoItem = (content: PicksContent): content is PicksPromo =>
  content.__typename === 'PicksPromo'

const isPicksProductCard = (card: PicksCardItem): card is PicksProductCard =>
  card.__typename === 'PicksProductCard'

const isPicksArticleCard = (card: PicksCardItem): card is PicksArticleCard =>
  card.__typename === 'PicksArticleCard'

const onClickHandler = (text: string, href: string, title: string) =>
  dynamicGa4DataLayerPush({
    event: 'click_link',
    item_text: text,
    item_parent_text: title,
    item_spec: 'energy in line article',
    item_url: href,
    utagid: '',
  })
